import {FC, useEffect, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import {ApiError} from '../../models/apiError';
import {
    COSTUME_CATEGORY_ICON_OPTIONS,
    COSTUME_RENDER_KINDS,
    CostumeRenderKind,
    CreateCostumeCategoryRequest,
    RENDER_KIND_LABEL_KO,
} from '../../types/costumeCategory';
import {useCreateCostumeCategory} from '../../hooks/useCostumeCategory';

const CODE_REGEX = /^[A-Z][A-Z0-9_]*$/;

const TRANSLATION_LANGS: Array<{lang: 'ko' | 'en' | 'ja' | 'vi'; label: string}> = [
    {lang: 'ko', label: '한국어'},
    {lang: 'en', label: 'English'},
    {lang: 'ja', label: '日本語'},
    {lang: 'vi', label: 'Tiếng Việt'},
];

interface Props {
    open: boolean;
    onClose: () => void;
    nextSortOrder: number;
}

export const EppiCategoryAddDialog: FC<Props> = ({open, onClose, nextSortOrder}) => {
    const [code, setCode] = useState('');
    const [renderKind, setRenderKind] = useState<CostumeRenderKind>('ARTIFACT');
    const [iconCode, setIconCode] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<number>(nextSortOrder);
    const [isActive, setIsActive] = useState(true);
    const [translations, setTranslations] = useState<Record<string, string>>({
        ko: '',
        en: '',
        ja: '',
        vi: '',
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const createMut = useCreateCostumeCategory();

    useEffect(() => {
        if (open) {
            setCode('');
            setRenderKind('ARTIFACT');
            setIconCode('');
            setSortOrder(nextSortOrder);
            setIsActive(true);
            setTranslations({ko: '', en: '', ja: '', vi: ''});
            setErrorMessage(null);
        }
    }, [open, nextSortOrder]);

    const codeValid = code.length === 0 || CODE_REGEX.test(code);
    const allTranslationsFilled = TRANSLATION_LANGS.every(
        (l) => translations[l.lang].trim().length > 0
    );
    const formValid =
        code.length > 0 &&
        codeValid &&
        allTranslationsFilled &&
        !createMut.isPending;

    const handleSubmit = async () => {
        const trimmedCode = code.trim();
        if (!CODE_REGEX.test(trimmedCode)) return;
        const req: CreateCostumeCategoryRequest = {
            code: trimmedCode,
            renderKind,
            displayNameKo: translations.ko.trim(),
            displayNameEn: translations.en.trim(),
            displayNameJa: translations.ja.trim(),
            displayNameVi: translations.vi.trim(),
            iconCode: iconCode.length > 0 ? iconCode : null,
            sortOrder,
            isActive,
        };
        try {
            await createMut.mutateAsync(req);
            onClose();
        } catch (e: unknown) {
            if (e instanceof ApiError) {
                setErrorMessage(e.message ?? '등록 실패');
            } else {
                setErrorMessage(e instanceof Error ? e.message : '등록 실패');
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>카테고리 추가</DialogTitle>
            <DialogContent dividers>
                <Stack gap={2} pt={1}>
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                    <TextField
                        label="code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        helperText={
                            codeValid
                                ? "^[A-Z][A-Z0-9_]*$ 형식 (예: STICKER_FOOD)"
                                : "대문자로 시작, 영문 대문자/숫자/언더스코어만 허용"
                        }
                        error={!codeValid}
                        size="small"
                        fullWidth
                        autoFocus
                    />

                    <TextField
                        select
                        label="렌더링 종류 (renderKind)"
                        value={renderKind}
                        onChange={(e) => setRenderKind(e.target.value as CostumeRenderKind)}
                        size="small"
                        fullWidth
                        helperText="카테고리에 속한 코스튬의 렌더/배치 방식 결정"
                    >
                        {COSTUME_RENDER_KINDS.map((k) => (
                            <MenuItem key={k} value={k}>
                                {RENDER_KIND_LABEL_KO[k]} ({k})
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="아이콘 (선택)"
                        value={iconCode}
                        onChange={(e) => setIconCode(e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">
                            <em>(없음)</em>
                        </MenuItem>
                        {COSTUME_CATEGORY_ICON_OPTIONS.map((opt) => (
                            <MenuItem key={opt.code} value={opt.code}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        type="number"
                        label="정렬 순서 (sortOrder)"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(Number(e.target.value))}
                        size="small"
                        fullWidth
                    />

                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        gap={1.5}
                        flexWrap="wrap"
                    >
                        {TRANSLATION_LANGS.map((l) => (
                            <TextField
                                key={l.lang}
                                label={`${l.label} (${l.lang})`}
                                value={translations[l.lang] ?? ''}
                                onChange={(e) =>
                                    setTranslations((prev) => ({
                                        ...prev,
                                        [l.lang]: e.target.value,
                                    }))
                                }
                                size="small"
                                sx={{minWidth: 200, flexGrow: 1}}
                                required
                            />
                        ))}
                    </Stack>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                        }
                        label="활성"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formValid}
                >
                    등록
                </Button>
            </DialogActions>
        </Dialog>
    );
};
